import type { Content, ContentHref, ContentRelation } from '@generalTypes/apiTypes';
import type { RootState } from '@generalTypes/rootStateTypes';
import type { RelationsToAndFromMap } from '@newStore/documentApi/documentApiTypes';
import { createTypedSelector, mapRelationsToRelationsToAndFrom } from '@newStore/genericHelpers';
import { referenceFrames } from '@newStore/referenceFrames/referenceFrames';

export const selectIsReferenceFrameLoaded = (state: RootState, referenceFrame: ContentHref) => {
  return Boolean(state.referenceFrames[referenceFrame]?.isLoaded);
};

export const selectProReferenceFrame = (state: RootState) => {
  return state.referenceFrames[referenceFrames.pro];
};

export const selectReferenceFrameContent = createTypedSelector(
  [(state) => state.referenceFrames, (state, referenceFrame: ContentHref) => referenceFrame],
  (referenceFramesData, referenceFrame) => {
    return referenceFramesData[referenceFrame]?.content;
  },
);

export const selectAllReferenceFrameData = createTypedSelector(
  [(state) => state.referenceFrames],
  (referenceFramesData) => {
    const allData: { content: Record<ContentHref, Content>; relations: ContentRelation[] } = {
      content: {},
      relations: [],
    };
    Object.values(referenceFramesData).forEach((referenceFrame) => {
      allData.content = { ...allData.content, ...referenceFrame.content };
      allData.relations = [...allData.relations, ...Object.values(referenceFrame.relations)];
    });
    return allData;
  },
);

export const selectReferenceFrameRelationsMap = createTypedSelector(
  [(state) => state.referenceFrames, (state, referenceFrame: ContentHref) => referenceFrame],
  (referenceFramesData, referenceFrame) => {
    const relations = referenceFramesData[referenceFrame]?.relations;
    if (!relations) {
      return null;
    }
    return mapRelationsToRelationsToAndFrom(relations);
  },
);

export const selectProReferenceFrameRelationsMap = createTypedSelector(
  [(state) => state.referenceFrames[referenceFrames.pro]?.relations],
  (relations) => {
    if (!relations) {
      return null;
    }
    return mapRelationsToRelationsToAndFrom(relations);
  },
);

/**
 * Returns the KRC content map from the reference frame store.
 * The KRC reference frame is always loaded (both in KRC_CURRICULUM and KRC_CURRICULUM_DISCIPLINE).
 */
export const selectKrcReferenceFrameContent = (state: RootState): Record<ContentHref, Content> => {
  return (
    state.referenceFrames[referenceFrames.krc]?.content ?? ({} as Record<ContentHref, Content>)
  );
};

/**
 * Returns the KRC relations map from the reference frame store.
 * The KRC reference frame is always loaded (both in KRC_CURRICULUM and KRC_CURRICULUM_DISCIPLINE).
 */
export const selectKrcReferenceFrameRelationsMap = createTypedSelector(
  [(state) => state.referenceFrames[referenceFrames.krc]?.relations],
  (relations): RelationsToAndFromMap | null => {
    if (!relations) {
      return null;
    }
    return mapRelationsToRelationsToAndFrom(relations);
  },
);
